import {MultiPolygon, Polygon} from "geojson";
import {GeometryIssue, SliverThresholds, SpikeThresholds} from "../../configuration/topology";

/**
 * Default spike angle threshold in degrees. Buffer-difference artifacts measure a
 * small fraction of a degree while genuine polygon corners are orders of magnitude
 * blunter, so 1° sits in the empty gap between the two populations.
 */
export const DEFAULT_SPIKE_ANGLE = 1;

/**
 * Default minimum area, in square meters, for {@link SliverThresholds.minArea}.
 */
export const DEFAULT_SLIVER_MIN_AREA = 0.05;

/**
 * Default maximum mean width, in meters, for {@link SliverThresholds.maxWidth}.
 */
export const DEFAULT_SLIVER_MAX_WIDTH = 0.1;

/**
 * Default tolerance for "these two vertices are the same vertex", expressed as a
 * fraction of the coordinate magnitude — i.e. in units of floating-point
 * resolution rather than of ground distance.
 *
 * A polygon clipper does not always close a ring on the exact coordinate it
 * started from: it can emit a vertex a handful of the last mantissa bits away.
 * Measured at real data magnitudes that is nanometres — a reproduction with
 * EPSG:3067 coordinates around 7.0e6 produced a vertex 6e-8 m from the ring's
 * start point, i.e. a relative offset of 9e-15. The pair is not an *exact*
 * duplicate, so an equality test keeps both, and the stub between them is far too
 * short to register as a spike by angle, so spike removal keeps them too. What is
 * left is a zero-area spur that renders as a splinter and makes the ring
 * self-intersecting.
 *
 * Being relative is what makes this projection- and magnitude-independent: the
 * same constant means ~7 micrometres at EPSG:3067 metre coordinates and ~6
 * micrometres at EPSG:4326 degrees, both roughly a thousand times the noise and
 * roughly a billion times below the spacing of any genuine pair of vertices.
 * A fixed ground tolerance could not do both — 1e-6 in degrees would be 11 cm.
 *
 * @default 1e-12
 */
export const DEFAULT_VERTEX_TOLERANCE_RELATIVE = 1e-12;

/**
 * Pure, projection-agnostic topology primitives. They operate on plain GeoJSON
 * coordinate arrays and know nothing about layers, features or the map — the
 * {@link Topology} module wires them to a layer feature.
 */

/**
 * Interior angle (degrees) at ring vertex `b`, between its neighbours `a` and `c`.
 * Returns `0` when either incident edge has zero length (a duplicate vertex), so
 * such vertices are always treated as removable.
 *
 * The angle is dimensionless, which is what makes every check built on it
 * independent of the layer's projection and units.
 */
export function vertexInteriorAngle(a: number[], b: number[], c: number[]): number {
    const ux = a[0] - b[0], uy = a[1] - b[1];
    const vx = c[0] - b[0], vy = c[1] - b[1];
    const lenU = Math.hypot(ux, uy);
    const lenV = Math.hypot(vx, vy);

    if (lenU === 0 || lenV === 0) {
        return 0;
    }

    let cosAngle = (ux * vx + uy * vy) / (lenU * lenV);
    // Guard against floating-point drift pushing |cos| slightly past 1.
    if (cosAngle > 1) cosAngle = 1; else if (cosAngle < -1) cosAngle = -1;

    return Math.acos(cosAngle) * 180 / Math.PI;
}

/**
 * Twice the (unsigned) area of the triangle a-b-c — the magnitude of the 2-D cross
 * product. Used as the spike size guard.
 */
export function triangleDoubleArea(a: number[], b: number[], c: number[]): number {
    return Math.abs((b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]));
}

/**
 * True when the ring vertex `b`, sitting between `a` and `c`, is a spike tip under
 * the given thresholds: the boundary doubles back on itself there (interior angle
 * at or below `angleDegrees`) and, when `maxArea` is finite, the triangle a-b-c is
 * small enough not to be a legitimate long-and-thin feature.
 */
export function isSpikeVertex(a: number[], b: number[], c: number[], angleDegrees: number, maxArea: number): boolean {
    if (vertexInteriorAngle(a, b, c) > angleDegrees) {
        return false;
    }
    return maxArea === Infinity || triangleDoubleArea(a, b, c) <= maxArea * 2;
}

/**
 * Indices of the spike vertices of a closed linear ring, in ring order. Detection
 * only — nothing is modified. Useful for reporting or highlighting before a repair.
 */
export function findRingSpikes(ring: number[][], options?: SpikeThresholds): number[] {
    const angleDegrees = typeof options?.angle === 'number' ? options.angle : DEFAULT_SPIKE_ANGLE;
    const maxArea = typeof options?.maxArea === 'number' ? options.maxArea : Infinity;

    if (!Array.isArray(ring) || ring.length < 4) {
        return [];
    }

    const isClosed = ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1];
    const vertices = isClosed ? ring.slice(0, -1) : ring;
    const spikes: number[] = [];

    for (let index = 0; index < vertices.length; index++) {
        const previous = vertices[(index - 1 + vertices.length) % vertices.length];
        const next = vertices[(index + 1) % vertices.length];

        if (isSpikeVertex(previous, vertices[index], next, angleDegrees, maxArea)) {
            spikes.push(index);
        }
    }

    return spikes;
}

/**
 * Removes spike vertices from a single closed linear ring, returning the cleaned
 * ring. Removal is iterated to a fixed point because deleting one tip can expose
 * the next, and the ring is never reduced below a valid closed triangle (four
 * coordinates including the repeated closing point).
 */
export function removeRingSpikes(ring: number[][], angleDegrees: number, maxArea: number): number[][] {
    // Need at least a closed triangle (3 distinct vertices + closing point).
    if (!Array.isArray(ring) || ring.length < 4) {
        return ring;
    }

    const isClosed = ring[0][0] === ring[ring.length - 1][0] &&
        ring[0][1] === ring[ring.length - 1][1];
    // Work on the open vertex list; the closing duplicate is re-added at the end.
    const vertices = isClosed ? ring.slice(0, -1) : ring.slice();

    let removedAny = true;
    while (removedAny && vertices.length > 3) {
        removedAny = false;
        for (let index = 0; index < vertices.length && vertices.length > 3; index++) {
            const count = vertices.length;
            const previous = vertices[(index - 1 + count) % count];
            const next = vertices[(index + 1) % count];

            if (isSpikeVertex(previous, vertices[index], next, angleDegrees, maxArea)) {
                vertices.splice(index, 1);
                removedAny = true;
                index--; // re-examine the vertex that shifted into this slot
            }
        }
    }

    vertices.push([vertices[0][0], vertices[0][1]]);
    return vertices;
}

/**
 * Removes spikes from the rings of a Polygon or MultiPolygon geometry.
 *
 * The input geometry is never mutated: cleaned coordinates are returned in a new
 * geometry object, together with the number of vertices removed so callers can
 * skip writing back an unchanged geometry. A non-area geometry is returned
 * untouched with `removed: 0`.
 *
 * @param geometry - GeoJSON Polygon or MultiPolygon geometry.
 * @param options - Optional {@link SpikeThresholds}.
 */
export function removeGeometrySpikes<T extends {type: string, coordinates: any}>(
    geometry: T,
    options?: SpikeThresholds
): {removed: number, geometry: T} {
    const angleDegrees = typeof options?.angle === 'number' ? options.angle : DEFAULT_SPIKE_ANGLE;
    const maxArea = typeof options?.maxArea === 'number' ? options.maxArea : Infinity;

    if (!geometry || (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon')) {
        return {removed: 0, geometry};
    }

    let removed = 0;
    const cleanRing = (ring: number[][]) => {
        const cleaned = removeRingSpikes(ring, angleDegrees, maxArea);
        removed += ring.length - cleaned.length;
        return cleaned;
    };

    const coordinates = geometry.type === 'Polygon'
        ? (geometry.coordinates as Polygon['coordinates']).map(cleanRing)
        : (geometry.coordinates as MultiPolygon['coordinates']).map(
            polygonCoordinates => polygonCoordinates.map(cleanRing)
        );

    return {removed, geometry: {...geometry, coordinates}};
}

/**
 * Mean width of an area geometry: `2 x area / perimeter`. For a long strip this is
 * (twice) the strip's thickness, which is exactly what tells a hair-thin clipper
 * sliver from a genuine narrow parcel — see {@link SliverThresholds.maxWidth}.
 * Both inputs must be measured in the same unit system (meters and square meters
 * here), which makes the result a real-world width rather than a projection
 * artifact.
 */
export function meanWidth(area: number, perimeter: number): number {
    return perimeter > 0 ? (2 * area) / perimeter : 0;
}

/**
 * True when a measured area/perimeter pair describes a **sliver**: either it
 * encloses no meaningful area at all, or it is a needle — long relative to how
 * thin it is. Either test alone is enough; see {@link SliverThresholds} for why
 * both are needed.
 *
 * @param area - Area in square meters.
 * @param perimeter - Perimeter in meters.
 * @param options - Optional {@link SliverThresholds}.
 */
export function isSliver(area: number, perimeter: number, options?: SliverThresholds): boolean {
    const minArea = typeof options?.minArea === 'number' ? options.minArea : DEFAULT_SLIVER_MIN_AREA;
    const maxWidth = typeof options?.maxWidth === 'number' ? options.maxWidth : DEFAULT_SLIVER_MAX_WIDTH;

    if (area <= minArea) {
        return true;
    }

    return maxWidth > 0 && meanWidth(area, perimeter) <= maxWidth;
}

/**
 * Unsigned planar (shoelace) area of a closed linear ring, in squared coordinate
 * units. Only ever used as a *degeneracy* test — "does this ring bound anything
 * at all" — which is why the planar formula is enough and no projection is needed.
 */
export function ringPlanarArea(ring: number[][]): number {
    if (!Array.isArray(ring) || ring.length < 4) {
        return 0;
    }

    let sum = 0;
    for (let index = 0, max = ring.length - 1; index < max; index++) {
        sum += ring[index][0] * ring[index + 1][1] - ring[index + 1][0] * ring[index][1];
    }
    return Math.abs(sum) / 2;
}

/**
 * True when two coordinates are the same vertex to within floating-point noise —
 * see {@link DEFAULT_VERTEX_TOLERANCE_RELATIVE} for why the test has to be relative
 * rather than an exact comparison or a fixed ground distance.
 *
 * @param a - First coordinate.
 * @param b - Second coordinate.
 * @param relativeTolerance - Fraction of the coordinate magnitude two vertices may
 * differ by and still count as one. Pass `0` for an exact equality test.
 */
export function verticesCoincide(a: number[], b: number[], relativeTolerance: number = DEFAULT_VERTEX_TOLERANCE_RELATIVE): boolean {
    if (a[0] === b[0] && a[1] === b[1]) {
        return true;
    }

    const magnitude = Math.max(Math.abs(a[0]), Math.abs(a[1]), Math.abs(b[0]), Math.abs(b[1]));
    const tolerance = magnitude * relativeTolerance;

    return Math.abs(a[0] - b[0]) <= tolerance && Math.abs(a[1] - b[1]) <= tolerance;
}

/**
 * Collapses runs of coincident consecutive coordinates in a closed linear ring,
 * i.e. removes its zero-length edges, and re-closes the result.
 *
 * Coincidence is judged by {@link verticesCoincide}, at floating-point resolution.
 * That is deliberately NOT exact equality: the artifact this exists to remove is a
 * clipper closing a ring a few mantissa bits away from where it started, which an
 * equality test misses entirely and which is too short to register as a spike by
 * angle. Genuinely distinct nearby vertices are many orders of magnitude further
 * apart and are never touched, and a ring that is already clean comes back with
 * identical numbers.
 *
 * Unlike {@link removeRingSpikes} there is no lower bound on the vertex count: a
 * ring that dedupes away to nothing is degenerate, and {@link isDegenerateRing} is
 * what reports that.
 *
 * @param ring - Closed linear ring.
 * @param relativeTolerance - Passed to {@link verticesCoincide}.
 */
export function dedupeRingVertices(ring: number[][], relativeTolerance: number = DEFAULT_VERTEX_TOLERANCE_RELATIVE): number[][] {
    if (!Array.isArray(ring) || ring.length === 0) {
        return ring;
    }

    const isClosed = ring.length > 1 && verticesCoincide(ring[0], ring[ring.length - 1], relativeTolerance);
    const vertices = isClosed ? ring.slice(0, -1) : ring.slice();

    const deduped: number[][] = [];
    for (const vertex of vertices) {
        const previous = deduped[deduped.length - 1];
        if (previous && verticesCoincide(previous, vertex, relativeTolerance)) {
            continue;
        }
        deduped.push(vertex);
    }

    // The wrap-around edge can be zero-length too — and this is the case the
    // clipper actually produces: the ring's last vertex sits a few mantissa bits
    // away from its first, so the boundary runs out to it and back for no distance.
    while (deduped.length > 1 && verticesCoincide(deduped[0], deduped[deduped.length - 1], relativeTolerance)) {
        deduped.pop();
    }

    if (deduped.length === 0) {
        return [];
    }

    deduped.push([deduped[0][0], deduped[0][1]]);
    return deduped;
}

/**
 * True when a closed linear ring cannot bound any area: fewer than three distinct
 * vertices, or a zero planar area (every vertex collinear). Such a ring is not a
 * small polygon, it is not a polygon at all — it must be dropped rather than
 * repaired.
 */
export function isDegenerateRing(ring: number[][]): boolean {
    const deduped = dedupeRingVertices(ring);
    // A valid closed ring needs 3 distinct vertices plus the repeated closing point.
    return deduped.length < 4 || ringPlanarArea(deduped) === 0;
}

/**
 * Signed area of the triangle (a, b, c) times two. Its sign gives the orientation
 * of the three points (positive = counter-clockwise), which is the primitive the
 * segment-intersection test is built on.
 */
export function crossProductZ(a: number[], b: number[], c: number[]): number {
    return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
}

/**
 * Squared euclidean distance between two coordinates. Squared so the common
 * "is it closer than t" test needs no square root.
 */
export function squaredDistance(coordA: number[], coordB: number[]): number {
    const deltaX = coordA[0] - coordB[0], deltaY = coordA[1] - coordB[1];
    return deltaX * deltaX + deltaY * deltaY;
}

/**
 * Squared distance from a point to a line segment, together with the projection of
 * the point onto the segment and the normalized segment parameter (0 =
 * segmentStart, 1 = segmentEnd, clamped).
 */
export function pointSegmentDistance(point: number[], segmentStart: number[], segmentEnd: number[]) {
    const directionX = segmentEnd[0] - segmentStart[0], directionY = segmentEnd[1] - segmentStart[1];
    const deltaX = point[0] - segmentStart[0], deltaY = point[1] - segmentStart[1];
    const segmentLengthSquared = directionX * directionX + directionY * directionY || 1e-12;
    let parameterT = (deltaX * directionX + deltaY * directionY) / segmentLengthSquared;
    parameterT = Math.max(0, Math.min(1, parameterT));
    const projectedX = segmentStart[0] + parameterT * directionX, projectedY = segmentStart[1] + parameterT * directionY;
    const differenceX = point[0] - projectedX, differenceY = point[1] - projectedY;
    return {
        distanceSquared: differenceX * differenceX + differenceY * differenceY,
        projection: [projectedX, projectedY],
        parameterT,
    };
}

/**
 * True when segments p1–p2 and p3–p4 PROPERLY cross — each segment has an endpoint
 * strictly on either side of the other (the classic bowtie). Mere touching (a
 * shared point, or a vertex lying on a segment) returns false on purpose: those
 * are the distance-based passes of {@link ringSelfIntersects}.
 */
export function segmentsProperlyCross(p1: number[], p2: number[], p3: number[], p4: number[]): boolean {
    const direction1 = crossProductZ(p3, p4, p1);
    const direction2 = crossProductZ(p3, p4, p2);
    const direction3 = crossProductZ(p1, p2, p3);
    const direction4 = crossProductZ(p1, p2, p4);

    return ((direction1 > 0 && direction2 < 0) || (direction1 < 0 && direction2 > 0)) &&
        ((direction3 > 0 && direction4 < 0) || (direction3 < 0 && direction4 > 0));
}

/**
 * True when a single closed ring is self-intersecting. Three passes:
 *
 * (1) a vertex lying ON a non-incident edge (a "touch"/spike — e.g. a vertex
 *     dragged or SNAPPED onto the polygon's own border),
 * (2) a vertex coinciding with a non-adjacent vertex (a pinch),
 * (3) two non-adjacent edges properly crossing (the classic bowtie).
 *
 * Passes (1) and (2) are distance-based, because a snapped coordinate sits on the
 * target edge only up to floating-point noise: a pure orientation / `=== 0` test
 * detects such touches only intermittently (they round to one side or the other).
 * With the default `tolerance` of `0` only exact contacts are reported, which is
 * the right setting for validating stored data; a live edit passes the map-unit
 * tolerance its snapping works at.
 *
 * @param ring - Closed linear ring (last coordinate repeats the first).
 * @param tolerance - Contact tolerance in coordinate units.
 */
export function ringSelfIntersects(ring: number[][], tolerance: number = 0): boolean {
    const edgeCount = ring.length - 1; // ring is closed (last == first)
    if (edgeCount < 3) return false;

    const toleranceSquared = tolerance * tolerance;

    // (1) Vertex on a non-incident edge (snapped/dragged onto the border).
    for (let vertexIndex = 0; vertexIndex < edgeCount; vertexIndex++) {
        const vertex = ring[vertexIndex];
        const previousEdge = (vertexIndex - 1 + edgeCount) % edgeCount;
        for (let edgeIndex = 0; edgeIndex < edgeCount; edgeIndex++) {
            // Skip the two edges incident to this vertex.
            if (edgeIndex === vertexIndex || edgeIndex === previousEdge) continue;
            const {distanceSquared, parameterT} =
                pointSegmentDistance(vertex, ring[edgeIndex], ring[edgeIndex + 1]);
            // Strictly interior contact only — coincidence with an edge ENDPOINT
            // is a vertex-vertex case handled by pass (2).
            if (distanceSquared <= toleranceSquared && parameterT > 0 && parameterT < 1) {
                return true;
            }
        }
    }

    // (2) Coincidence with a non-adjacent vertex (pinch point).
    for (let firstVertex = 0; firstVertex < edgeCount; firstVertex++) {
        for (let secondVertex = firstVertex + 1; secondVertex < edgeCount; secondVertex++) {
            const adjacent = secondVertex === firstVertex + 1 ||
                (firstVertex === 0 && secondVertex === edgeCount - 1);
            if (adjacent) continue;
            if (squaredDistance(ring[firstVertex], ring[secondVertex]) <= toleranceSquared) {
                return true;
            }
        }
    }

    // (3) Proper crossings between non-adjacent edges (bowtie).
    for (let firstEdge = 0; firstEdge < edgeCount; firstEdge++) {
        const firstStart = ring[firstEdge];
        const firstEnd = ring[firstEdge + 1];

        for (let secondEdge = firstEdge + 1; secondEdge < edgeCount; secondEdge++) {
            // Edges sharing a vertex are adjacent — skip them. The first and last
            // edge of a closed ring also share the closing vertex.
            const adjacent = secondEdge === firstEdge + 1 ||
                (firstEdge === 0 && secondEdge === edgeCount - 1);
            if (adjacent) continue;

            if (segmentsProperlyCross(firstStart, firstEnd, ring[secondEdge], ring[secondEdge + 1])) {
                return true;
            }
        }
    }

    return false;
}

/**
 * The rings of a Polygon or MultiPolygon geometry, grouped per part, so both types
 * can be walked by one loop. A non-area geometry yields an empty list.
 */
export function areaGeometryParts(geometry: {type: string, coordinates: any} | null | undefined): number[][][][] {
    if (!geometry) {
        return [];
    }
    if (geometry.type === 'Polygon') {
        return [geometry.coordinates as Polygon['coordinates']];
    }
    if (geometry.type === 'MultiPolygon') {
        return geometry.coordinates as MultiPolygon['coordinates'];
    }
    return [];
}

/**
 * Every linear ring of a Polygon or MultiPolygon geometry — outlines and holes,
 * every part — in one flat list. That is the shape the snapping passes below want:
 * a vertex has to be welded to whichever boundary happens to run next to it, and a
 * hole's border is just as much a boundary as an outline.
 */
export function areaGeometryRings(geometry: {type: string, coordinates: any} | null | undefined): number[][][] {
    return areaGeometryParts(geometry).flat();
}

/**
 * Largest absolute coordinate of an area geometry — the magnitude a *relative*
 * tolerance is scaled by (see {@link DEFAULT_SNAP_TOLERANCE_RELATIVE}). `0` for a
 * geometry with no coordinates.
 */
export function areaGeometryMagnitude(geometry: {type: string, coordinates: any} | null | undefined): number {
    let magnitude = 0;
    for (const ring of areaGeometryRings(geometry)) {
        for (const vertex of ring) {
            const largest = Math.max(Math.abs(vertex[0]), Math.abs(vertex[1]));
            if (largest > magnitude) {
                magnitude = largest;
            }
        }
    }
    return magnitude;
}

/**
 * Default gap-closing tolerance for {@link snapAreaGeometry}, expressed as a
 * fraction of the coordinate magnitude rather than as a ground distance — the same
 * device, and for the same reason, as {@link DEFAULT_VERTEX_TOLERANCE_RELATIVE},
 * just three orders of magnitude coarser because it has a real (if tiny) gap to
 * close rather than floating-point noise to absorb.
 *
 * The gap it exists for is the corridor a buffer-and-difference cut leaves between
 * the parts of a split polygon: {@link Analysis.splitPolygon} subtracts the cutting
 * line buffered by 0.0001 m, so the parts end up ~0.0002 m apart *on the ground* —
 * measured at real data magnitudes that is 4.2e-4 map units in EPSG:3857 around
 * 60°N (where a map unit is about half a metre) and 2.0e-4 in EPSG:3067. Relative
 * to the coordinate magnitude those are 4.8e-11 and 2.9e-11, so `1e-9` clears both
 * by more than an order of magnitude while still landing on a real-world distance
 * of a few millimetres:
 *
 * - EPSG:3857 at ~8.8e6 -> 8.8e-3 map units, ~4 mm on the ground,
 * - EPSG:3067 at ~7.0e6 -> 7.0e-3 m,
 * - EPSG:4326 at ~60 -> 6.0e-8°, ~7 mm.
 *
 * A fixed tolerance in map units could not do that: 7e-3 means millimetres in a
 * metre projection and 800 km in degrees. And a few millimetres is far below any
 * gap that means something — two parcels are drawn snapped to each other, so a real
 * gap between them is metres, not millimetres.
 *
 * @default 1e-9
 */
export const DEFAULT_SNAP_TOLERANCE_RELATIVE = 1e-9;

/**
 * The point of `targetRings` nearest to `point`, or `null` when nothing lies within
 * `toleranceSquared` of it.
 *
 * Target *vertices* win over points projected onto a target *edge* whenever both are
 * in range, even if the projection is marginally closer. Landing on the existing
 * vertex welds the two boundaries at a point they both already have; landing beside
 * it would instead add a new vertex a fraction of a millimetre away and leave the
 * pair of them as a spur.
 */
function nearestPointOnRings(point: number[], targetRings: number[][][], toleranceSquared: number): number[] | null {
    let best: number[] | null = null;
    let bestDistance = toleranceSquared;

    for (const ring of targetRings) {
        for (const candidate of ring) {
            const distance = squaredDistance(point, candidate);
            if (distance <= bestDistance) {
                bestDistance = distance;
                best = [candidate[0], candidate[1]];
            }
        }
    }

    if (best) {
        return best;
    }

    bestDistance = toleranceSquared;
    for (const ring of targetRings) {
        for (let index = 0; index < ring.length - 1; index++) {
            const {distanceSquared, projection} = pointSegmentDistance(point, ring[index], ring[index + 1]);
            if (distanceSquared <= bestDistance) {
                bestDistance = distanceSquared;
                best = projection;
            }
        }
    }

    return best;
}

/**
 * What {@link snapRingToRings} did to a ring: the cleaned ring plus how many
 * vertices it had to move and how many it had to add, so a caller can tell whether
 * the ring was touched at all.
 */
export type SnapRingResult = {ring: number[][], moved: number, inserted: number};

/**
 * Welds one closed linear ring onto the boundary described by `targetRings`, moving
 * nothing further than `tolerance`.
 *
 * Two passes, and BOTH are needed for the boundaries to come out exactly coincident:
 *
 * 1. **move** — every ring vertex within `tolerance` of the target boundary is
 *    placed onto it ({@link nearestPointOnRings}), so the ring no longer runs
 *    *beside* the target but *along* it,
 * 2. **insert** — every target vertex that lies within `tolerance` of one of the
 *    ring's segments, and is not already a vertex of it, is added to that segment at
 *    its own position.
 *
 * Without pass 2 the shared stretch would still be two different polylines — the
 * ring's vertices sit on the target's edges, but the target's own corners are
 * missing from the ring, so wherever the target bends the ring cuts the corner and
 * a hairline gap survives. With both passes the shared stretch of the ring is the
 * target's boundary subdivided at extra collinear points, which a polygon clipper
 * treats as the same line.
 *
 * The ring is never mutated, and a ring with nothing in range comes back with
 * identical numbers.
 *
 * @param ring - Closed linear ring to weld.
 * @param targetRings - Boundary to weld it onto, as returned by {@link areaGeometryRings}.
 * @param tolerance - Maximum distance, in coordinate units, a vertex may be moved.
 */
export function snapRingToRings(ring: number[][], targetRings: number[][][], tolerance: number): SnapRingResult {
    if (!Array.isArray(ring) || ring.length < 4 || !(tolerance > 0) || targetRings.length === 0) {
        return {ring, moved: 0, inserted: 0};
    }

    const toleranceSquared = tolerance * tolerance;
    const isClosed = ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1];
    // Work on the open vertex list; the closing duplicate is re-added at the end.
    let vertices = isClosed ? ring.slice(0, -1) : ring.slice();

    let moved = 0;
    vertices = vertices.map((vertex) => {
        const target = nearestPointOnRings(vertex, targetRings, toleranceSquared);
        if (!target || (target[0] === vertex[0] && target[1] === vertex[1])) {
            return vertex;
        }
        moved++;
        return target;
    });

    const insertions = new Map<number, {parameterT: number, coordinate: number[]}[]>();
    const count = vertices.length;

    for (const targetRing of targetRings) {
        // The closing duplicate would be considered twice, to no effect.
        const targetVertices = targetRing.length > 1 &&
            targetRing[0][0] === targetRing[targetRing.length - 1][0] &&
            targetRing[0][1] === targetRing[targetRing.length - 1][1]
            ? targetRing.slice(0, -1)
            : targetRing;

        for (const point of targetVertices) {
            // Already present — pass 1 moved a ring vertex exactly onto it.
            if (vertices.some((vertex) => squaredDistance(vertex, point) <= toleranceSquared)) {
                continue;
            }

            let bestDistance = toleranceSquared;
            let bestSegment = -1;
            let bestParameterT = 0;

            for (let index = 0; index < count; index++) {
                const {distanceSquared, parameterT} =
                    pointSegmentDistance(point, vertices[index], vertices[(index + 1) % count]);
                // Strictly interior only: contact with a segment ENDPOINT is the
                // vertex case just ruled out above.
                if (parameterT <= 0 || parameterT >= 1 || distanceSquared > bestDistance) {
                    continue;
                }
                bestDistance = distanceSquared;
                bestSegment = index;
                bestParameterT = parameterT;
            }

            if (bestSegment < 0) {
                continue;
            }

            const list = insertions.get(bestSegment) ?? [];
            list.push({parameterT: bestParameterT, coordinate: [point[0], point[1]]});
            insertions.set(bestSegment, list);
        }
    }

    let inserted = 0;
    if (insertions.size > 0) {
        const rebuilt: number[][] = [];
        for (let index = 0; index < count; index++) {
            rebuilt.push(vertices[index]);
            const list = insertions.get(index);
            if (!list) {
                continue;
            }
            // Along the segment, or the ring would double back on itself.
            list.sort((first, second) => first.parameterT - second.parameterT);
            for (const item of list) {
                rebuilt.push(item.coordinate);
                inserted++;
            }
        }
        vertices = rebuilt;
    }

    vertices.push([vertices[0][0], vertices[0][1]]);
    return {ring: vertices, moved, inserted};
}

/**
 * What {@link snapAreaGeometry} did: the welded geometry and the vertex counts of
 * both passes, `0`/`0` when the two geometries were never close enough to interact.
 */
export type SnapAreaGeometryResult<T> = {geometry: T, movedVertices: number, insertedVertices: number};

/**
 * Welds a Polygon or MultiPolygon onto the boundary of another one, so that a
 * hairline gap between the two closes and a union of them comes back as ONE
 * polygon instead of a MultiPolygon.
 *
 * Only `geometry` moves — `target` is the reference and keeps its coordinates
 * exactly — and no vertex moves further than `tolerance`. Neither input is mutated.
 * A non-area geometry, an empty target or a non-positive tolerance are all no-ops.
 *
 * @param geometry - Geometry to weld.
 * @param target - Geometry to weld it onto; left untouched.
 * @param tolerance - Maximum distance, in coordinate units, a vertex may be moved.
 */
export function snapAreaGeometry<T extends {type: string, coordinates: any}>(
    geometry: T,
    target: {type: string, coordinates: any} | null | undefined,
    tolerance: number
): SnapAreaGeometryResult<T> {
    if (!geometry || (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon') || !(tolerance > 0)) {
        return {geometry, movedVertices: 0, insertedVertices: 0};
    }

    const targetRings = areaGeometryRings(target);
    if (targetRings.length === 0) {
        return {geometry, movedVertices: 0, insertedVertices: 0};
    }

    let movedVertices = 0;
    let insertedVertices = 0;

    const snapRings = (rings: number[][][]) => rings.map((ring) => {
        const {ring: snapped, moved, inserted} = snapRingToRings(ring, targetRings, tolerance);
        movedVertices += moved;
        insertedVertices += inserted;
        return snapped;
    });

    const coordinates = geometry.type === 'Polygon'
        ? snapRings(geometry.coordinates as Polygon['coordinates'])
        : (geometry.coordinates as MultiPolygon['coordinates']).map(snapRings);

    return {geometry: {...geometry, coordinates}, movedVertices, insertedVertices};
}

/**
 * Measures one linear ring, in real-world units — square meters and meters. The
 * sliver tests need a *geodesic* measurement (a mean width in projection units
 * would mean something different in every projection), which only the map knows
 * how to produce, so the pure code takes it as a callback.
 */
export type MeasureRing = (ring: number[][]) => {area: number, perimeter: number};

/**
 * Options for {@link sanitizeAreaGeometry}: the spike {@link SpikeThresholds}
 * plus the sliver thresholds, where `false` switches sliver removal off entirely
 * and leaves only the spike / degeneracy cleaning.
 */
export type SanitizeAreaGeometryOptions = SpikeThresholds & {
    slivers?: SliverThresholds | false
};

/**
 * What {@link sanitizeAreaGeometry} removed, plus the cleaned geometry — `null`
 * when nothing of substance was left (every part was a sliver).
 */
export type SanitizeAreaGeometryResult<T> = {
    geometry: T | null,
    removedVertices: number,
    removedRings: number,
    removedParts: number
};

/**
 * Removes every artifact a polygon clipper can leave behind from a Polygon or
 * MultiPolygon geometry, in one pass per ring:
 *
 * 1. **spikes** — needle protrusions ({@link removeGeometrySpikes}),
 * 2. **duplicate vertices** — zero-length edges ({@link dedupeRingVertices}),
 * 3. **degenerate rings** — rings bounding no area ({@link isDegenerateRing});
 *    a part whose *exterior* ring is degenerate is dropped whole,
 * 4. **sliver holes** — hair-thin inner rings ({@link isSliver}),
 * 5. **sliver parts** — hair-thin members of a MultiPolygon.
 *
 * That is the full artifact set produced when two nearly-coincident boundaries are
 * intersected, differenced or unioned — the drawn purchase-area overlap case.
 *
 * The input geometry is never mutated; the cleaned geometry is returned as a new
 * object of the same `type` (a MultiPolygon reduced to one part stays a
 * MultiPolygon), together with counts so a caller can tell whether anything
 * changed. A non-area geometry is returned untouched.
 *
 * @param geometry - GeoJSON Polygon or MultiPolygon geometry.
 * @param measureRing - Real-world {@link MeasureRing} for the sliver tests.
 * @param options - Optional {@link SanitizeAreaGeometryOptions}.
 */
export function sanitizeAreaGeometry<T extends {type: string, coordinates: any}>(
    geometry: T,
    measureRing: MeasureRing,
    options?: SanitizeAreaGeometryOptions
): SanitizeAreaGeometryResult<T> {
    if (!geometry || (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon')) {
        return {geometry, removedVertices: 0, removedRings: 0, removedParts: 0};
    }

    const angleDegrees = typeof options?.angle === 'number' ? options.angle : DEFAULT_SPIKE_ANGLE;
    const maxArea = typeof options?.maxArea === 'number' ? options.maxArea : Infinity;
    const sliverThresholds = options?.slivers === false ? false : (options?.slivers ?? {});

    let removedVertices = 0;
    let removedRings = 0;
    let removedParts = 0;

    const cleanedParts: number[][][][] = [];

    for (const part of areaGeometryParts(geometry)) {
        const cleanedRings: number[][][] = [];
        let partDropped = false;

        for (let ringIndex = 0; ringIndex < part.length; ringIndex++) {
            const ring = part[ringIndex];

            // Dedupe FIRST: a near-coincident vertex pair is a zero-length stub, and
            // the interior angle measured across it is meaningless — the clipper's
            // 60-nanometre closing offset reads as a blunt ~8 degrees, so the spike
            // pass would keep it. Collapsing the pair first both removes the spur and
            // lets the spike test see the ring's real corners. Deduping again after
            // spike removal catches pairs that only become adjacent once a tip goes.
            let cleaned = dedupeRingVertices(ring);
            cleaned = removeRingSpikes(cleaned, angleDegrees, maxArea);
            cleaned = dedupeRingVertices(cleaned);
            removedVertices += Math.max(0, ring.length - cleaned.length);

            if (isDegenerateRing(cleaned)) {
                // An exterior ring that bounds nothing takes its whole part with it,
                // holes included; a degenerate hole is simply not a hole.
                if (ringIndex === 0) {
                    partDropped = true;
                    break;
                }
                removedRings++;
                removedVertices += cleaned.length;
                continue;
            }

            if (ringIndex > 0 && sliverThresholds !== false) {
                const {area, perimeter} = measureRing(cleaned);
                if (isSliver(area, perimeter, sliverThresholds)) {
                    removedRings++;
                    removedVertices += cleaned.length;
                    continue;
                }
            }

            cleanedRings.push(cleaned);
        }

        if (partDropped || cleanedRings.length === 0) {
            removedParts++;
            removedVertices += part.reduce((count, ring) => count + ring.length, 0);
            continue;
        }

        if (sliverThresholds !== false) {
            const exterior = measureRing(cleanedRings[0]);
            // The AREA test uses the net area (holes subtracted) so a shape whose
            // hole eats it up is still rejected; the WIDTH test uses the exterior
            // ring alone, so a legitimate ring-shaped parcel — large outline, large
            // hole, narrow frame — is judged on its outline and never mistaken for
            // a needle.
            const holesArea = cleanedRings.slice(1)
                .reduce((sum, ring) => sum + measureRing(ring).area, 0);

            if (isSliver(exterior.area - holesArea, exterior.perimeter, sliverThresholds)) {
                removedParts++;
                removedVertices += cleanedRings.reduce((count, ring) => count + ring.length, 0);
                continue;
            }
        }

        cleanedParts.push(cleanedRings);
    }

    if (cleanedParts.length === 0) {
        return {geometry: null, removedVertices, removedRings, removedParts};
    }

    const coordinates = geometry.type === 'Polygon' ? cleanedParts[0] : cleanedParts;

    return {
        geometry: {...geometry, coordinates},
        removedVertices,
        removedRings,
        removedParts
    };
}

/**
 * Every defect of a Polygon or MultiPolygon geometry, in geometry order —
 * detection only, nothing is modified. This is the analysis behind
 * {@link Topology.validateGeometry}; {@link sanitizeAreaGeometry} is the repair
 * for the subset that can be repaired.
 *
 * @param geometry - GeoJSON geometry to inspect (any type; a non-area geometry is
 * reported as `not-area` rather than silently passing).
 * @param measureRing - Real-world {@link MeasureRing} for the sliver tests.
 * @param options - Spike / sliver thresholds and the self-intersection
 * `tolerance` in map units.
 */
export function findAreaGeometryIssues(
    geometry: {type: string, coordinates: any} | null | undefined,
    measureRing: MeasureRing,
    options?: SpikeThresholds & SliverThresholds & {tolerance?: number, vertexTolerance?: number}
): GeometryIssue[] {
    const issues: GeometryIssue[] = [];

    if (!geometry) {
        issues.push({type: 'empty', message: 'The feature has no geometry.'});
        return issues;
    }

    if (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon') {
        issues.push({
            type: 'not-area',
            message: `Geometry type "${geometry.type}" is not a Polygon or MultiPolygon.`
        });
        return issues;
    }

    const parts = areaGeometryParts(geometry);

    if (parts.length === 0) {
        issues.push({type: 'empty', message: 'The geometry has no coordinates.'});
        return issues;
    }

    const sliverThresholds: SliverThresholds = {minArea: options?.minArea, maxWidth: options?.maxWidth};
    const spikeThresholds: SpikeThresholds = {angle: options?.angle, maxArea: options?.maxArea};
    const tolerance = typeof options?.tolerance === 'number' ? options.tolerance : 0;
    const vertexTolerance = typeof options?.vertexTolerance === 'number' ? options.vertexTolerance : DEFAULT_VERTEX_TOLERANCE_RELATIVE;

    parts.forEach((part, partIndex) => {
        if (part.length === 0) {
            issues.push({
                type: 'empty',
                part: partIndex,
                message: `Part ${partIndex} has no rings.`
            });
            return;
        }

        part.forEach((ring, ringIndex) => {
            const location = geometry.type === 'MultiPolygon'
                ? `part ${partIndex}, ${ringIndex === 0 ? 'exterior ring' : `hole ${ringIndex}`}`
                : `${ringIndex === 0 ? 'exterior ring' : `hole ${ringIndex}`}`;

            if (ring.length > 1 &&
                (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1])) {
                issues.push({
                    type: 'unclosed-ring',
                    part: partIndex,
                    ring: ringIndex,
                    message: `The ${location} is not closed.`
                });
            }

            // Coincidence is judged at floating-point resolution, not by equality, so
            // validation reports exactly what the repair collapses. A clipper's
            // near-duplicate closing vertex would otherwise surface only indirectly,
            // as a `self-intersection` that no repair claims to be able to fix.
            for (let index = 1; index < ring.length; index++) {
                if (!verticesCoincide(ring[index], ring[index - 1], vertexTolerance)) {
                    continue;
                }
                // The closing coordinate legitimately repeats the first one.
                if (index === ring.length - 1 && ring.length > 2) continue;
                issues.push({
                    type: 'duplicate-vertex',
                    part: partIndex,
                    ring: ringIndex,
                    coordinate: ring[index],
                    message: `The ${location} repeats the vertex [${ring[index]}].`
                });
            }

            // The vertex BEFORE the closing coordinate coinciding with the first one is
            // the same defect on the wrap-around edge, and is the shape the clipper
            // actually emits.
            if (ring.length > 3 && verticesCoincide(ring[0], ring[ring.length - 2], vertexTolerance)) {
                issues.push({
                    type: 'duplicate-vertex',
                    part: partIndex,
                    ring: ringIndex,
                    coordinate: ring[ring.length - 2],
                    message: `The ${location} closes on a vertex it already visits at [${ring[0]}].`
                });
            }

            if (isDegenerateRing(ring)) {
                issues.push({
                    type: 'degenerate-ring',
                    part: partIndex,
                    ring: ringIndex,
                    message: `The ${location} does not bound any area.`
                });
                // Everything below assumes a ring that bounds something.
                return;
            }

            findRingSpikes(ring, spikeThresholds).forEach((vertexIndex) => {
                issues.push({
                    type: 'spike',
                    part: partIndex,
                    ring: ringIndex,
                    coordinate: ring[vertexIndex],
                    message: `The ${location} has a spike at [${ring[vertexIndex]}].`
                });
            });

            if (ringSelfIntersects(ring, tolerance)) {
                issues.push({
                    type: 'self-intersection',
                    part: partIndex,
                    ring: ringIndex,
                    message: `The ${location} intersects itself.`
                });
            }

            const {area, perimeter} = measureRing(ring);
            if (isSliver(area, perimeter, sliverThresholds)) {
                issues.push({
                    type: 'sliver',
                    part: partIndex,
                    ring: ringIndex,
                    area,
                    width: meanWidth(area, perimeter),
                    message: `The ${location} is a sliver: ${area.toFixed(3)} m² with a mean width of ${meanWidth(area, perimeter).toFixed(3)} m.`
                });
            }
        });
    });

    return issues;
}
